/**
 * Single analytics interface for the whole site.
 *
 * Call sites only ever use `analytics.track(...)` — the concrete provider is
 * wired up inside this module, so switching providers means editing this file
 * (and the mounting component in components/shared/DeferredVercelAnalytics.tsx),
 * never the call sites.
 *
 * Events fired before the provider is ready (it mounts at browser idle) are
 * buffered and flushed on init instead of being dropped.
 */

export type AnalyticsEventProps = Record<
  string,
  string | number | boolean | null | undefined
>;

export interface AnalyticsProvider {
  track: (event: string, props?: AnalyticsEventProps) => void;
}

const MAX_BUFFERED_EVENTS = 50;

let provider: AnalyticsProvider | null = null;
let buffer: Array<[string, AnalyticsEventProps | undefined]> = [];

function track(event: string, props?: AnalyticsEventProps): void {
  if (typeof window === 'undefined') return;
  if (provider) {
    provider.track(event, props);
    return;
  }
  if (buffer.length < MAX_BUFFERED_EVENTS) {
    buffer.push([event, props]);
  }
}

function setProvider(next: AnalyticsProvider): void {
  provider = next;
  const pending = buffer;
  buffer = [];
  for (const [event, props] of pending) {
    next.track(event, props);
  }
}

/**
 * Current provider: Vercel Web Analytics. Loaded dynamically so the SDK never
 * enters the shared first-load chunk; the <Analytics/> component (mounted by
 * DeferredVercelAnalytics) must already be in the tree so events are accepted.
 */
export async function initAnalyticsProvider(): Promise<void> {
  if (typeof window === 'undefined' || provider) return;
  try {
    const mod = await import('@vercel/analytics');
    setProvider({ track: mod.track });
  } catch {
    // Analytics must never break the app; events simply stop buffering.
    buffer = [];
  }
}

export const analytics = { track };
